import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sparkles, Check, Loader2, LogIn, Twitter, Linkedin, Gift } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHARE_TEXT = "I just found this — Everything PDF lets you generate NDAs, leases, invoices, resumes and 20+ other docs in a browser. AI fills the form for you. Free 👇";
const SHARE_URL = "https://pdf-forms-hub.emergent.host";

function openShare(network) {
  const url = encodeURIComponent(SHARE_URL);
  const text = encodeURIComponent(SHARE_TEXT);
  let target = '';
  if (network === 'twitter') {
    target = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
  } else if (network === 'linkedin') {
    target = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
  }
  window.open(target, '_blank', 'noopener,noreferrer,width=600,height=520');
}

export default function ReferralModal({ open, onClose }) {
  const { user, login, refresh } = useAuth();
  const [shared, setShared] = useState({ twitter: false, linkedin: false });
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const handleShare = (net) => {
    openShare(net);
    setShared((s) => ({ ...s, [net]: true }));
  };

  const canClaim = (shared.twitter || shared.linkedin) && !!user && !claimed;

  const claim = async () => {
    if (!user) {
      login(window.location.pathname);
      return;
    }
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/referral/claim`);
      await refresh();
      setClaimed(true);
      toast.success(`🎉 ${res.data.days_granted} days of Pro added to your account!`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not claim reward';
      toast.error(typeof msg === 'string' ? msg : 'Could not claim reward');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white border-stone-200" data-testid="referral-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-700 grid place-items-center mb-3">
            <Gift size={22} />
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Get <span className="text-orange-600">1 month of Pro</span> — free
          </DialogTitle>
          <DialogDescription className="text-stone-600 text-sm leading-relaxed pt-1">
            Share Everything PDF on Twitter or LinkedIn and we'll instantly unlock 30 days of Pro for you. No watermarks, unlimited AI drafts.
          </DialogDescription>
        </DialogHeader>

        {!claimed && (
          <div className="space-y-3 mt-2">
            <button
              onClick={() => handleShare('twitter')}
              data-testid="ref-share-twitter"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium transition-colors"
            >
              <span className="flex items-center gap-2"><Twitter size={16} /> Share on Twitter / X</span>
              {shared.twitter && <Check size={16} className="text-emerald-400" />}
            </button>
            <button
              onClick={() => handleShare('linkedin')}
              data-testid="ref-share-linkedin"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#0a66c2] hover:bg-[#084e96] text-white text-sm font-medium transition-colors"
            >
              <span className="flex items-center gap-2"><Linkedin size={16} /> Share on LinkedIn</span>
              {shared.linkedin && <Check size={16} className="text-emerald-200" />}
            </button>

            <div className="pt-2">
              {!user ? (
                <button
                  onClick={() => login(window.location.pathname)}
                  data-testid="ref-login-cta"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium btn-clay disabled:opacity-50"
                >
                  <LogIn size={14} /> Sign in to claim your free month
                </button>
              ) : (
                <button
                  onClick={claim}
                  disabled={!canClaim || claiming}
                  data-testid="ref-claim-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium btn-clay disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {claiming ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {canClaim ? 'I shared — give me my free month' : 'Share first to unlock the button'}
                </button>
              )}
            </div>

            <button
              onClick={onClose}
              data-testid="ref-dismiss"
              className="w-full text-xs text-stone-400 hover:text-stone-600 mt-1"
            >
              Maybe later
            </button>
          </div>
        )}

        {claimed && (
          <div className="text-center py-4" data-testid="ref-claimed">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-3">
              <Check size={26} />
            </div>
            <h3 className="font-display font-bold text-xl tracking-tight">You're Pro for 30 days!</h3>
            <p className="text-stone-600 text-sm mt-2">Refresh the editor — watermarks are gone and AI is unlocked.</p>
            <button
              onClick={onClose}
              className="mt-5 px-5 py-2.5 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium btn-clay"
            >
              Awesome, let's go
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
